import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Logo from "../assets/images/fugus-logo.webp";
import PaymentMethod from "../assets/images/payment.png";
import PlayStore from "../assets/images/play-store.png";
import AppStore from "../assets/images/app-store.png";
import FssaiLogo from "../assets/images/fssai-logo.svg";
import Copyright from "../pages/shared/copyright/Copyright";

const footerLinkSections = [
  {
    id: "about",
    title: "About",
    duration: 400,
    links: [
      { label: "About Us", href: "" },
      { label: "Contact Us", href: "" },
      { label: "Our Stores", href: "" },
    ],
  },
  {
    id: "support",
    title: "Customer Support",
    duration: 600,
    links: [
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Terms & Conditions", href: "/terms-and-conditions" },
      { label: "FAQ", href: "" },
    ],
  },
  {
    id: "specialties",
    title: "Our Specialties",
    duration: 800,
    links: [
      { label: "Free Shipping", href: "" },
      { label: "Customer Support", href: "" },
      { label: "Secure Checkout", href: "" },
    ],
  }
];

const FooterLink = ({ href, children }) =>
  href ? (
    <Link to={href} className="text-heading hover-text-main-600">
      {children}
    </Link>
  ) : (
    <span className="text-heading">{children}</span>
  );

const Footer = () => {
  const [openSection, setOpenSection] = useState("information");
  const [isMobile, setIsMobile] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const footerContainerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: shouldReduceMotion ? 0 : 0.08,
      },
    },
  };

  const footerItemVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 8 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.25 : 0.65,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  const mobilePanelVariants = {
    hidden: { height: 0, opacity: 0.35 },
    visible: {
      height: "auto",
      opacity: 1,
      transition: {
        duration: shouldReduceMotion ? 0.2 : 0.35,
        ease: [0.32, 0.72, 0, 1],
        when: "beforeChildren",
        staggerChildren: shouldReduceMotion ? 0 : 0.04,
        delayChildren: shouldReduceMotion ? 0 : 0.03,
      },
    },
  };

  const mobileLinkVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 4 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.15 : 0.25,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleViewport = (event) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleViewport);
      return () => mediaQuery.removeEventListener("change", handleViewport);
    }

    mediaQuery.addListener(handleViewport);
    return () => mediaQuery.removeListener(handleViewport);
  }, []);

  const toggleSection = (sectionId) => {
    if (!isMobile) return;
    setOpenSection((currentOpen) =>
      currentOpen === sectionId ? "" : sectionId,
    );
  };

  return (
    <footer className="footer py-40 d-none d-lg-block">
      <div className="container container-lg">
        <motion.div
          className="footer-item-wrapper footer-layout d-flex align-items-start flex-wrap"
          variants={footerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          <motion.div
            className="footer-item footer-item--about"
            variants={footerItemVariants}
          >
            <div className="max-w-340">
              <div className="footer-item__logo">
                <a href="index.php">
                  {" "}
                  <img src={Logo} alt="" />
                </a>
              </div>
              {/* <p className="mb-28 text-heading">
                We're Grocery Shop, an innovative team of food supliers.
              </p> */}
              <div className="d-flex flex-column gap-8">
                <p className="text-heading fw-medium">
                  Ground Floor, Ward No. 16, Door No 446B, 446 C, FUGUS
                  BUILDING, Elbo Road, Puthuvype, Ernakulam, Kerala - 682508
                </p>
                {/* <a
                  href="mailto:support@example.com"
                  className="text-heading fw-medium hover-text-main-600"
                >
                  support@example.com
                </a>
                <a
                  href="tel:+(406)555-0120"
                  className="text-heading fw-medium hover-text-main-600"
                >
                  + (406) 555-0120
                </a> */}
              </div>
            </div>
          </motion.div>
          {footerLinkSections.map((section) => {
            const isOpen = !isMobile || openSection === section.id;
            const panelId = `footer-panel-${section.id}`;

            return (
              <motion.div
                key={section.id}
                className="footer-item footer-item--links"
                variants={footerItemVariants}
              >
                <button
                  type="button"
                  className={`footer-item__title footer-accordion__trigger ${
                    isOpen ? "is-open" : ""
                  }`}
                  onClick={() => toggleSection(section.id)}
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                >
                  <span>{section.title}</span>
                  <i className="ph ph-caret-down footer-accordion__icon" />
                </button>
                {isMobile ? (
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.ul
                        id={panelId}
                        className="footer-menu footer-accordion__panel is-open"
                        exit={{ height: 0, opacity: 0.35 }}
                        variants={mobilePanelVariants}
                        initial="hidden"
                        animate="visible"
                        style={{ overflow: "hidden" }}
                      >
                        {section.links.map((link) => (
                          <motion.li
                            key={link.label}
                            variants={mobileLinkVariants}
                          >
                            <FooterLink href={link.href}>
                              {link.label}
                            </FooterLink>
                          </motion.li>
                        ))}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                ) : (
                  <ul
                    id={panelId}
                    className="footer-menu footer-accordion__panel is-open"
                  >
                    {section.links.map((link) => (
                      <li key={link.label}>
                        <FooterLink href={link.href}>
                          {link.label}
                        </FooterLink>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            );
          })}
          <motion.div
            className="footer-item footer-item--app"
            variants={footerItemVariants}
          >
            <h6 className="footer-item__title">Shop on The Go</h6>
            <p className="mb-16">Fugu's Fresh App is available. Get it now</p>
            <div className="my-32 footer-app">
              <div className="flex-align gap-8 footer-app__main">
                <div className="bg-white rounded-10 p-1 box-shadow-5xl">
                  <img
                    src={FssaiLogo}
                    alt="FSSAI"
                  />
                </div>
                <div className="d-flex flex-row flex-md-column gap-16 footer-app__stores">
                  <a
                    href=""
                    className="footer-app__store-btn  d-flex justify-content-center align-items-center fw-medium text-heading text-sm  hover-text-white  rounded-6"
                  >
                    <img src={AppStore} alt="Play Store" />
                  </a>
                  <a
                    href=""
                    className="footer-app__store-btn  d-flex justify-content-center align-items-center gap-8 fw-medium text-heading text-sm hover-text-white  rounded-6"
                  >
                    <img src={PlayStore} alt="Play Store" />
                  </a>
                </div>
              </div>
              <div className="mt-24 footer-app__payments">
                <img src={PaymentMethod} alt="Method " />
              </div>
            </div>
          </motion.div>
        </motion.div>
        <Copyright />
      </div>
    </footer>
  );
};

export default Footer;
